/*
 * Shared Tagalys Recommendations connector wrapper.
 *
 * window.TagalysRecommendations.fetch(opts) — the single hardened caller of the
 * headless Recommendations connector (Tagalys.APIConnector.Recommendations), used
 * by BOTH product-carousel.liquid's Tagalys fallback tabs and the mini-bag "You
 * Might Also Like" upsells. Loaded ONCE, defer, in layout/theme.liquid — never
 * per-section. Reuses window.ensureTagalysSdk (tagalys-sdk-loader.js) and mirrors
 * the confirmed-working configure() body of collection-tagalys.liquid /
 * search-tagalys.liquid.
 *
 * window.TagalysRecommendations.correctAvailability(products, container, done) —
 * shared with the same two callers (was duplicated verbatim in both before
 * TASK-47920's DRY pass). See the function itself for why the correction exists.
 *
 * window.TagalysProductFormat — small pure formatting helpers (escHtml/money/
 * readMetaValue/pickMetafield/shopifySrcset) shared by every renderer that turns
 * a raw Tagalys product object into card markup: collection-tagalys-card.liquid
 * (Collection/SearchResults connectors) and mini-bag-recommendations-script.liquid
 * (Recommendations connector). Kept here rather than a new asset file so there's
 * no extra global request — this file is already loaded on every page.
 *
 * Headline risk is SILENT FAILURE (a wrong id or wrong accessor renders an empty
 * area with no error — the resolved TECH-DEBT #4 class), so every call is wrapped
 * in try/catch + an 8s timeout + a '[Tagalys Rec]' console prefix; on
 * empty/failed/absent-id the caller's onEmpty/onError fires and the surface
 * degrades to empty (never a broken layout — L-TR-4).
 *
 * ANCHOR AUTO-DETECT (confirmed live 2026-07-22): different Tagalys recommendation
 * types are configured server-side to either require the current product as an
 * anchor ("Similar Products" 400s without one) or reject one outright ("New
 * Products" and other store-wide feeds 400 WITH one) — there is no way to tell
 * which from the theme. Callers now just offer an anchor whenever they have one
 * (`productIds`); this wrapper tries the last-known-good mode for that
 * recommendation ID first (persisted in localStorage — survives a full-page
 * navigation, unlike a JS-memory cache), or, with nothing learned yet, tries WITH
 * the anchor first when one is available (a required anchor 400s immediately if
 * omitted, so anchored-first resolves either type in at most one retry). Replaces
 * the old per-block "Scope to current product" checkbox, which had no way to catch
 * a merchant picking the wrong setting for a given ID (that exact bug shipped
 * live). `onProducts(products, meta)` reports `meta.anchored` so a caller that
 * cares knows which mode actually won. If a required-anchor mode is already
 * learned and this call has none to offer (e.g. the populated mini-bag channel's
 * page-load fetch on a genuinely empty cart), the request is skipped outright —
 * the retry only swaps modes when there's an anchor to toggle, so a zero-anchor
 * call against a known anchor-required ID would otherwise 400 every time.
 *
 * NOTE (OQ1, resolved 2026-07-21): Tagalys.APIConnector.Recommendations.new(...)
 * .helpers.call() — the shape that works for the Collection/SearchResults
 * connectors — throws inside the SDK's own getRequestOptions() ("Cannot read
 * properties of undefined (reading 'params')"): this.requestOptions is never
 * populated on an instance constructed via .new(). Confirmed live in devtools
 * against api-connector-3.0.11.min.js. The static one-shot
 * Tagalys.APIConnector.Recommendations.call({params, onSuccess, onFailure})
 * builds and dispatches the request in a single step and works correctly —
 * confirmed live, returns real products. Use .call(), not .new().helpers.call(),
 * for this connector specifically.
 */
;(function () {
  if (window.TagalysRecommendations) return;            // dedup guard (FAILS §3)
  var CONFIGURED = false;
  var SCOPED_CACHE_PREFIX = 'tagalys_rec_anchored:';    // localStorage key per recommendationId

  function applyConfiguration(token, domain) {
    Tagalys.setConfiguration({
      api: { serverUrl: 'https://api-r4.tagalys.com' },
      currency: { fractionalDigits: 2, forceFractionalDigits: true },
      analyticsStorageConsentProvided: function () {
        return window.Shopify && window.Shopify.customerPrivacy
          ? window.Shopify.customerPrivacy.analyticsProcessingAllowed() : false; },
      track: true
    });
    var marketCountry = (document.body && document.body.getAttribute('data-market-country')) || 'GB';
    Tagalys.setPlatformConfiguration({
      storefrontAPI: { accessToken: token, myShopifyDomain: domain },
      countryCode: marketCountry, baseCountryCode: 'GB',
      metafields: { products: [
        { namespace: 'custom', key: 'product_type' },
        { namespace: 'custom', key: 'taste_notes' },
        { namespace: 'custom', key: 'format' },
        { namespace: 'custom', key: 'flavour' },
        { namespace: 'custom', key: 'organic' },
        { namespace: 'custom', key: 'caffeine_free' }
      ] }
    });
  }

  function configure(token, domain) {                    // mirrors collection/search initTagalys
    if (CONFIGURED) return;
    applyConfiguration(token, domain);
    CONFIGURED = true;
    if (window.TagalysConsent) {
      window.TagalysConsent.onChange(function () { applyConfiguration(token, domain); });
    }
  }

  function readAnchoredMode(recId) {
    try {
      var v = window.localStorage.getItem(SCOPED_CACHE_PREFIX + recId);
      return v === null ? null : v === '1';
    } catch (e) { return null; }
  }
  function writeAnchoredMode(recId, anchored) {
    try { window.localStorage.setItem(SCOPED_CACHE_PREFIX + recId, anchored ? '1' : '0'); } catch (e) {}
  }

  // Shopify's configured currency punctuation and display rule for the active
  // currency, probed server-side and stamped onto <body> by layout/theme.liquid —
  // the single source of truth, also used by snippets/money.liquid. Intl's own
  // defaults follow the visitor's browser locale (punctuation) and CLDR (digit
  // count), neither of which is guaranteed to agree with the shop's configuration.
  var body = document.body;
  var CURRENCY_DECIMAL_SEPARATOR = (body && body.getAttribute('data-currency-decimal-separator')) || '.';
  var CURRENCY_GROUP_SEPARATOR = body ? body.getAttribute('data-currency-group-separator') || '' : ',';
  var CURRENCY_DECIMAL_DIGITS = parseInt((body && body.getAttribute('data-currency-decimal-digits')) || '2', 10);
  var CURRENCY_SYMBOL_PREFIX = (body && body.getAttribute('data-currency-symbol-prefix')) || '';
  var CURRENCY_APPEND_CODE = !!(body && body.getAttribute('data-currency-append-code') === 'true');

  window.TagalysProductFormat = {
    escHtml: function (s) {
      return String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    },
    // symbolOnly: true for the price-facet slider, a filter control rather than a
    // purchase-facing price. Omitted (the default) for an actual product price —
    // fails safe, since a future caller that forgets the flag gets the correct,
    // fully-disambiguated format rather than silently reverting to symbol-only.
    money: function (pounds, currency, symbolOnly) {
      if (!currency) return parseFloat(pounds).toFixed(2);
      if (symbolOnly) {
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency', currency: currency, currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: CURRENCY_DECIMAL_DIGITS, maximumFractionDigits: CURRENCY_DECIMAL_DIGITS
          }).format(pounds);
        } catch (e) {
          return currency + ' ' + parseFloat(pounds).toFixed(2);
        }
      }
      try {
        var parts = new Intl.NumberFormat(undefined, {
          style: 'currency', currency: currency, currencyDisplay: 'code',
          minimumFractionDigits: CURRENCY_DECIMAL_DIGITS, maximumFractionDigits: CURRENCY_DECIMAL_DIGITS
        }).formatToParts(pounds);
        var number = '', code = '', sign = '';
        parts.forEach(function (p) {
          if (p.type === 'currency') code = p.value;
          else if (p.type === 'literal') return; // Intl's own code/number spacing — rebuilt manually below
          else if (p.type === 'minusSign') sign = '-';
          else if (p.type === 'decimal') number += CURRENCY_DECIMAL_SEPARATOR;
          else if (p.type === 'group') number += CURRENCY_GROUP_SEPARATOR;
          else number += p.value;
        });
        code = code || currency;
        return sign + CURRENCY_SYMBOL_PREFIX + number + (CURRENCY_APPEND_CODE ? ' ' + code : '');
      } catch (e) {
        return currency + ' ' + parseFloat(pounds).toFixed(2);
      }
    },
    // A Tagalys metafield can come back as a plain value, a { value } wrapper, a
    // { text } wrapper, or a metaobject reference ({ name, image }), not just a
    // bare string.
    readMetaValue: function (value) {
      if (value == null) return '';
      if (typeof value === 'object') {
        if (value.value != null && value.value !== '') return value.value;
        if (value.text != null && value.text !== '') return value.text;
        if (value.name != null && value.name !== '') return value.name;
      }
      return value;
    },
    pickMetafield: function (meta, keys) {
      var readMetaValue = window.TagalysProductFormat.readMetaValue;
      if (!meta || !keys || !keys.length) return '';
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        var direct = readMetaValue(meta[key]);
        if (direct != null && direct !== '') return direct;
        var nested = meta;
        var parts = key.split('.');
        for (var j = 0; j < parts.length; j++) {
          if (nested == null) break;
          nested = nested[parts[j]];
        }
        nested = readMetaValue(nested);
        if (nested != null && nested !== '') return nested;
      }
      return '';
    },
    shopifySrcset: function (src, widths) {
      var escHtml = window.TagalysProductFormat.escHtml;
      if (!src || !/cdn\.shopify\.com/.test(src)) return '';
      var sep = src.indexOf('?') >= 0 ? '&' : '?';
      return (widths || [390, 480, 640, 800]).map(function (w) {
        return escHtml(src + sep + 'width=' + w) + ' ' + w + 'w';
      }).join(', ');
    }
  };

  window.TagalysRecommendations = {
    // o: {recommendationId, productIds, count, token, domain, sdkUrl, onProducts, onEmpty, onError}
    // onProducts(products, meta) — meta.anchored reports whether the anchor was
    // actually used for the request that succeeded.
    fetch: function (o) {
      if (!o || !o.recommendationId) { o && o.onEmpty && o.onEmpty(); return; }
      var recId  = o.recommendationId;
      var anchor = (o.productIds && o.productIds.length) ? o.productIds : [];
      var done = false, timer = setTimeout(function () {
        if (done) return; done = true;
        console.error('[Tagalys Rec] timed out', recId);
        o.onError && o.onError(new Error('timeout'));
      }, o.timeoutMs || 8000);
      window.ensureTagalysSdk(o.sdkUrl, function () {
        try {
          configure(o.token, o.domain);

          var cachedAnchored   = readAnchoredMode(recId);
          var tryAnchoredFirst = cachedAnchored !== null ? cachedAnchored : anchor.length > 0;

          // A recommendation already confirmed (elsewhere, any earlier successful
          // call) to require an anchor can never succeed with none to offer — the
          // retry-on-failure below only swaps modes when there's an anchor to
          // toggle, so a zero-anchor call here would 400 deterministically every
          // time (e.g. the populated mini-bag channel's page-load fetch on a
          // genuinely empty cart). Skip the doomed request instead of firing and
          // failing it.
          if (cachedAnchored === true && !anchor.length) {
            if (done) return; done = true; clearTimeout(timer);
            o.onEmpty && o.onEmpty();
            return;
          }

          function attempt(useAnchor, isRetry) {
            var anchored = useAnchor && anchor.length > 0;
            var params = { recommendationId: recId, count: o.count || 12, include: ['product_ids', 'products'] };
            if (anchored) params.productIds = anchor;
            Tagalys.APIConnector.Recommendations.call({
              params: params,
              onSuccess: function (response, helpers) {
                if (done) return; done = true; clearTimeout(timer);
                writeAnchoredMode(recId, anchored);
                var products = (helpers && helpers.getProducts && helpers.getProducts()) || [];
                if (products.length) { o.onProducts && o.onProducts(products, { anchored: anchored }); }
                else {
                  console.warn('[Tagalys Rec] empty result', recId, 'anchor:', anchored ? anchor : 'none');
                  o.onEmpty && o.onEmpty();
                }
              },
              onFailure: function (r) {
                // A 400 usually means the wrong anchor mode for this recommendation ID
                // (see ANCHOR AUTO-DETECT above) — retry the other mode once before
                // surfacing failure, but only if there's actually an anchor to toggle.
                if (!isRetry && anchor.length) { attempt(!useAnchor, true); return; }
                if (done) return; done = true; clearTimeout(timer);
                console.error('[Tagalys Rec] call failed', recId, r);
                o.onError && o.onError(r);
              }
            });
          }

          attempt(tryAnchoredFirst, false);
        } catch (e) {
          if (done) return; done = true; clearTimeout(timer);
          console.error('[Tagalys Rec] connector threw (accessor undefined?)', e);   // TECH-DEBT #4 class
          o.onError && o.onError(e);
        }
      });
    },

    // Tagalys's recommendations API can report a product unavailable when it
    // genuinely isn't — confirmed live: a product whose OWN Tagalys admin panel
    // and the real Shopify Storefront both say available:true still came back
    // available:false from this exact endpoint. Originally scoped to bundle
    // products only (mirroring collection-tagalys-bundle-availability.liquid's
    // PLP correction, since a bundle's real purchasability depends on its
    // component variants, not a single inventory count) — broadened to every
    // unavailable product after that finding, since the staleness isn't
    // bundle-specific. Shared by product-carousel.liquid's Tagalys fallback
    // tabs and mini-bag-recommendations-script.liquid's upsells (was
    // duplicated verbatim in both — TASK-47920).
    //
    // ALSO enforces the same warehouse-zero rule as warehouse-available.liquid /
    // collection-tagalys-badges.liquid's applyAvailability() (TASK-54815): a
    // recommendations rail sat outside every one of that fix's mechanisms,
    // since it renders from raw Tagalys JSON via string concatenation rather
    // than through product-card.liquid or a DOM node collection-tagalys-badges
    // can hydrate — a product with zero Warehouse stock but nonzero
    // retail-store stock (Shopify's blended availableForSale still true) could
    // render addable here and fail at checkout, the exact defect TASK-54815
    // exists to prevent. Checked for EVERY target now, not only ones Tagalys
    // marked unavailable — the widened query still batches all of a caller's
    // targets in the one request. Final rule, matching warehouse-available.liquid
    // exactly: corrected = liveAvailableForSale AND (warehouse state unknown OR
    // warehouse quantity > 0). A known-positive or not-yet-backfilled warehouse
    // figure never forces a card available on its own; only a known zero forces
    // it unavailable, overriding both Tagalys's cache and the live Storefront
    // check in that one direction.
    //
    // Checks the SPECIFIC variant (p.variants[0].id — the same one both callers
    // actually add to cart / link to), not the product as a whole. Product-level
    // availableForSale is true if ANY variant is sellable, so a multi-variant
    // product with only its other variants excluded from the shopper's market
    // (e.g. a larger format with no shipping-profile coverage there) still
    // resolved true at the product level and wrongly overrode Tagalys's correct
    // per-variant available:false — confirmed live (TASK-47920). Products
    // without a resolvable variant id are left uncorrected (Tagalys's own
    // available:false stands) rather than guessed at.
    correctAvailability: function (products, container, done) {
      // Matches D28's PLP equivalent (docs/DECISIONS.md) — both callers here
      // request count: 12, so this only needs to cover that plus headroom,
      // not 4x it.
      var AVAILABILITY_CORRECTION_MAX_TARGETS = 25;
      var token = container.getAttribute('data-bundle-token');
      if (!token) { done(products); return; }

      var targets = products.filter(function (p) {
        return p && p.variants && p.variants[0] && p.variants[0].id;
      }).slice(0, AVAILABILITY_CORRECTION_MAX_TARGETS);
      if (!targets.length) { done(products); return; }

      var version = container.getAttribute('data-bundle-api-version') || '2026-07';
      // @inContext(country:) is required — without it this always resolves
      // availableForSale against the shop's default market, not the shopper's
      // actual one. Confirmed live: a product excluded from a market by its
      // shipping profile came back available:true here and overwrote Tagalys's
      // correct available:false for that market, showing a purchasable card for
      // a product that isn't actually purchasable there.
      var marketCountry = (document.body && document.body.getAttribute('data-market-country')) || 'GB';
      var variables = { country: marketCountry };
      var fieldDecls = targets.map(function (p, idx) {
        var varName = 'v' + idx;
        variables[varName] = 'gid://shopify/ProductVariant/' + p.variants[0].id;
        return 'p' + idx + ': node(id: $' + varName + ') { ... on ProductVariant { id availableForSale metafield(namespace: "custom", key: "warehouse_stock") { value } product { bundleMf: metafield(namespace: "custom", key: "is_bundle") { value } customBundleMf: metafield(namespace: "custom", key: "is_custom_bundle") { value } } } }';
      });
      var varDecls = targets.map(function (p, idx) { return '$v' + idx + ': ID!'; });
      var query = 'query AvailabilityCheck($country: CountryCode!, ' + varDecls.join(', ') + ') @inContext(country: $country) { ' + fieldDecls.join(' ') + ' }';

      var ac = window.AbortController ? new AbortController() : null;
      var timer = setTimeout(function () { if (ac) ac.abort(); }, 8000);

      fetch('/api/' + version + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query: query, variables: variables }),
        signal: ac ? ac.signal : undefined
      })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (json) {
          clearTimeout(timer);
          if (!json || !json.data) { done(products); return; }
          var resultByVariantGid = {};
          Object.keys(json.data).forEach(function (k) {
            var node = json.data[k];
            if (!node || !node.id) return;
            var mf = node.metafield;
            var raw = (mf && mf.value !== null && mf.value !== undefined && mf.value !== '') ? Number(mf.value) : null;
            var whKnown = raw !== null && isFinite(raw);
            // A bundle (fixed or custom) always ships as one gift-wrapped unit
            // regardless of any one line's own stock, so the warehouse-only rule
            // never applies to it - same exception as warehouse-available.liquid.
            var bundleMfs = node.product;
            var isBundleProduct = bundleMfs && ((bundleMfs.bundleMf && bundleMfs.bundleMf.value === 'true') || (bundleMfs.customBundleMf && bundleMfs.customBundleMf.value === 'true'));
            if (isBundleProduct) whKnown = false;
            resultByVariantGid[node.id] = {
              available: node.availableForSale === true,
              whKnown: whKnown,
              whQty: whKnown ? raw : null
            };
          });
          products.forEach(function (p) {
            var vid = p && p.variants && p.variants[0] && p.variants[0].id;
            if (!vid) return;
            var r = resultByVariantGid['gid://shopify/ProductVariant/' + vid];
            if (!r) return; // outside the batch cap — leave Tagalys's own value
            p.available = r.whKnown ? (r.available && r.whQty > 0) : r.available;
          });
          done(products);
        })
        .catch(function () {
          clearTimeout(timer);
          done(products); // passive safety net — leave Tagalys's state on any failure
        });
    }
  };
})();
